import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getEnv } from '@app/helpers/helpers';
import { I18nService } from '@app/translations/i18n';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

const CHATBOT_ENVS = {
  dev: 'https://cdn.brainaihub.tech/infocert/uat/',
  prod: 'https://cdn.brainaihub.tech/infocert/prod/',
};

const CHATBOT_IDS = {
  dev: '01J21NP7JK8JNE3V4DGPCJKTXF',
  prod: '01J29B5MYC2BJFCM3KC6AFQZXR',
};

@Injectable({
  providedIn: 'root',
})
export class LivechatService {
  private loadedScript = false;
  private widgetID = 'widget-my-sign';
  private chatbotDataID = CHATBOT_IDS.prod;
  chatbotStatus = '';
  private isChatbotOpen = false;
  /** true se l'ultima chiusura è stata dal bottone header (pallino visibile), false se dalla X del widget. */
  private closedFromHeader = false;
  private chatbotStatusSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  public chatbotStatus$: Observable<string> =
    this.chatbotStatusSubject.asObservable();
  private widgetVisibilitySubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);
  public widgetVisibility$: Observable<boolean> =
    this.widgetVisibilitySubject.asObservable();
  private closeTooltip = new Subject<void>();
  closeTooltip$ = this.closeTooltip.asObservable();

  CURRENT_CHATBOT_ENV: 'dev' | 'prod' = getEnv();

  CHATBOT_HOURS_JSON = 'https://cdn.brainaihub.tech/gosign.json';
  CHATBOT_SCRIPTS_DOMAIN = CHATBOT_ENVS.prod;

  constructor(
    private http: HttpClient,
    private i18nService: I18nService
  ) {
    this.CHATBOT_SCRIPTS_DOMAIN = CHATBOT_ENVS[this.CURRENT_CHATBOT_ENV];
    this.chatbotDataID = CHATBOT_IDS[this.CURRENT_CHATBOT_ENV];
  }

  public async checkWidgetStatus(): Promise<boolean> {
    const isVisible = await this.getWidgetStatus();
    this.updateWidgetVisibility(isVisible);
    return isVisible;
  }

  getWidgetStatus(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http
        .get<ChatBotDays>(`${this.CHATBOT_HOURS_JSON}?${Date.now()}`)
        .subscribe(
          response => {
            const days = response?.days || [];
            const currentDay = moment().day();
            const currentTime = moment().format('HH:mm:ss');

            const isActive = days.some(day => {
              const start = moment(day.start, 'HH:mm:ss');
              const end = moment(day.end, 'HH:mm:ss');
              const weekdays = day.weekdays || [];

              if (weekdays.includes(currentDay)) {
                return (
                  currentTime >= start.format('HH:mm:ss') &&
                  currentTime <= end.format('HH:mm:ss')
                );
              }

              return false;
            });

            resolve(isActive);
          },
          error => {
            reject(error);
          }
        );
    });
  }

  loadChatBot(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const language = this.i18nService.language;
      this.loadScript('1.0.0', language).then(() => resolve(true));
    });
  }

  /** Aggiorna la lingua del widget e ricarica impostazioni (API widget: refresh). */
  public setWidgetLanguage(lang: string): void {
    const el = document.getElementById(this.widgetID) as { settings?: { lang?: string }; refresh?: () => Promise<void> } | null;
    if (!el?.settings) return;
    const langCode = lang.split('_')[0];
    el.settings = { ...el.settings, lang: langCode };
    if (typeof el.refresh === 'function') {
      el.refresh();
    }
  }

  public async loadScript(version: string, language: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.loadedScript) {
        this.loadedScript = true;

        const widgetScriptUrl =
          'https://widget-agent-trusty.infocert.it/widget.js';
        if (!document.querySelector(`script[src="${widgetScriptUrl}"]`)) {
          const t = document.createElement('script');
          t.type = 'text/javascript';
          t.async = true;
          t.src = widgetScriptUrl;
          const n = document.getElementsByTagName('script')[0];
          n.parentNode?.insertBefore(t, n);
        }

        if (!document.getElementById(this.widgetID)) {
          const d = document.createElement('agent-widget') as any;
          d.setAttribute('id', this.widgetID);
          d.agent = '01KJ7K46TDSP8MS57J7A8R4TPB';
          d.tenant = '01JEVG3MRWRXG39JVBVV8AA936';
          const langCode = (language || this.i18nService.language).split('_')[0];
          d.settings = {
            api: 'api-trusty-hub.infocert.it',
            backend: 'api-trusty.infocert.it',
            lang: langCode,
            secure: true,
            mode: 'widget',
            voice: false,
            upload: false,
            history: false,
          };
          this.applyWidgetPosition(d);
          document.body.appendChild(d);
          setTimeout(() => this.applyWidgetPosition(d), 0);
          setTimeout(() => this.applyWidgetPosition(d), 500);
        }

        const widgetElement = document.getElementById(this.widgetID);
        widgetElement.addEventListener('visible', (e: CustomEvent<[boolean]>) => {
          const [open] = e.detail ?? [false];
          this.isChatbotOpen = open;
          if (open) {
            this.chatbotStatus = 'open';
            this.closedFromHeader = false;
          } else {
            this.chatbotStatus = this.closedFromHeader ? 'minimize' : 'closed';
            this.closedFromHeader = false;
          }
          this.chatbotStatusSubject.next(this.chatbotStatus);
        });

        window.addEventListener('resize', () => {
          const element = document.getElementById(this.widgetID) as any;
          if (element) {
            this.applyWidgetPosition(element);
          }
        });
      }
      resolve(true);
    });
  }

  public updateWidgetVisibility(
    isVisible: boolean,
    force: boolean = false
  ): void {
    if (force) {
      this.widgetVisibilitySubject.next(isVisible);
    }
    this.widgetVisibilitySubject.next(isVisible);
  }

  /** Usa l'API del widget: toggle(open?: boolean). Chiusura dal bottone header → pallino; chiusura dalla X del widget → no pallino. */
  public toggleChatBot(mode: 'open' | 'close' | 'toggle' = 'toggle'): void {
    if (!this.widgetVisibilitySubject.getValue()) return;

    const widgetElement = document.getElementById(this.widgetID) as { toggle?: (open?: boolean) => void } | null;
    if (!widgetElement?.toggle) return;

    const willClose = mode === 'close' || (mode === 'toggle' && this.isChatbotOpen);
    if (willClose) {
      this.closedFromHeader = true;
    }

    if (mode === 'open') {
      widgetElement.toggle(true);
    } else if (mode === 'close') {
      widgetElement.toggle(false);
    } else {
      widgetElement.toggle();
    }
  }

  private applyWidgetPosition(widgetElement: any): void {
    const trigger = document.getElementById('chatBotButton');
    if (!trigger || !widgetElement) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const buttonOffset = 0;
    const contentOffset = 16;
    const top = Math.max(0, rect.top + buttonOffset);
    const right = Math.max(0, window.innerWidth - rect.right);
    const contentTop = Math.max(0, rect.bottom + contentOffset);

    widgetElement.settings = {
      ...(widgetElement.settings || {}),
      styles: {
        ...(widgetElement.settings?.styles || {}),
        button: {
          ...(widgetElement.settings?.styles?.button || {}),
          position: 'fixed',
          top: `${top}px`,
          right: `${right}px`,
          bottom: 'auto',
          left: 'auto',
          display: 'none',
        },
        content: {
          ...(widgetElement.settings?.styles?.content || {}),
          position: 'fixed',
          top: `${contentTop}px`,
          right: `${right}px`,
          bottom: 'auto',
          left: 'auto',
          minHeight: '620px',
          height: '620px',
        },
      },
    };
  }

  closeTooltipComponent() {
    this.closeTooltip.next();
  }
}

interface ChatBotDays {
  days: Array<{
    start: string;
    end: string;
    weekdays: number[];
  }>;
}
